import {
	faCopy,
	faPaste,
	faSave,
	faScissors,
	faSliders,
	faTrash,
	faCircleQuestion
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ButtonAtom } from "../../../../components/atoms/atom";
import { BOQ_STATUS } from "../../../../constants/boq-status";
import BoQAddDropdown from "./BoQAddDropdown";
import BoQValidationPanel from "./BoQValidationPanel";

/**
 * Cosuno-style BoQ editor toolbar: Add dropdown, copy/cut/paste/delete/more, Discard & Save.
 * Subgroup option is disabled when canAddSubgroup is false (no group exists yet).
 */
const BoQToolbar = ({
	onAddElement,
	canAddSubgroup = false,
	onCopy,
	onCut,
	onPaste,
	onDelete,
	onMore,
	onDefineSchema,
	onStartTour,
	onDiscard,
	onSaveDraft,
	onValidateAndFinalize,
	onReopenBoQ,
	hasInvoice = false,
	hasUnsavedChanges = false,
	hasOzErrors = false,
	isSaving = false,
	/** ValidationResult from the last save (null = not yet validated / stale after user edit). */
	validationResult = null,
	/** Fires when the user clicks an error row in the validation panel to navigate to the element. */
	onValidationElementClick,
	/** True while the Validate & Finalize validation call is in-flight. */
	isVFValidating = false,
	boqStatus = null,
	canDelete = false,
	canCopy = false,
	canPaste = false,
}) => {
	const { t } = useTranslation();
	const noop = () => { };
	const isFinalized = boqStatus === BOQ_STATUS.FINALIZED;
	const [panelOpen, setPanelOpen] = useState(false);
	const badgeRef = useRef(null);

	return (
		<div className="boq-toolbar">
			<div className="boq-toolbar-left">
				<span data-tour="boq-add">
					<BoQAddDropdown
						className="boq-toolbar-add-dropdown"
						onAddElement={onAddElement}
						canAddSubgroup={canAddSubgroup}
						toggleVariant="primary"
						toggleSize="sm"
						disabled={isFinalized}
					/>
				</span>
				<span data-tour="boq-edit-actions" className="d-inline-flex">
					<OverlayTrigger placement="top" overlay={<Tooltip id="boq-toolbar-copy">{t("boq.toolbar.copy")}</Tooltip>}>
						<span className="d-inline-block">
							<ButtonAtom
								className="boq-toolbar-icon-btn"
								onClick={onCopy ?? noop}
								disabled={!canCopy || isFinalized}
							>
								<FontAwesomeIcon icon={faCopy} />
							</ButtonAtom>
						</span>
					</OverlayTrigger>
					<OverlayTrigger placement="top" overlay={<Tooltip id="boq-toolbar-cut">{t("boq.toolbar.cut")}</Tooltip>}>
						<span className="d-inline-block">
							<ButtonAtom
								className="boq-toolbar-icon-btn"
								onClick={onCut ?? noop}
								disabled={!canCopy || isFinalized}
							>
								<FontAwesomeIcon icon={faScissors} />
							</ButtonAtom>
						</span>
					</OverlayTrigger>
					<OverlayTrigger placement="top" overlay={<Tooltip id="boq-toolbar-paste">{t("boq.toolbar.paste")}</Tooltip>}>
						<span className="d-inline-block">
							<ButtonAtom
								className="boq-toolbar-icon-btn"
								onClick={onPaste ?? noop}
								disabled={!canPaste || isFinalized}
							>
								<FontAwesomeIcon icon={faPaste} />
							</ButtonAtom>
						</span>
					</OverlayTrigger>
					<OverlayTrigger placement="top" overlay={<Tooltip id="boq-toolbar-delete">{t("boq.toolbar.delete")}</Tooltip>}>
						<span className="d-inline-block">
							<ButtonAtom
								className="boq-toolbar-icon-btn"
								onClick={onDelete ?? noop}
								disabled={!canDelete || isFinalized}
							>
								<FontAwesomeIcon icon={faTrash} />
							</ButtonAtom>
						</span>
					</OverlayTrigger>
				</span>
				{!isFinalized && (
					<OverlayTrigger placement="top" overlay={<Tooltip id="boq-toolbar-schema">{t("boq.toolbar.define-schema")}</Tooltip>}>
						<span className="d-inline-block ms-2" data-tour="boq-define-schema">
							<ButtonAtom
								className="boq-toolbar-icon-btn"
								onClick={onDefineSchema ?? noop}
							>
								<FontAwesomeIcon icon={faSliders} />
							</ButtonAtom>
						</span>
					</OverlayTrigger>
				)}
			</div>
			<div className="boq-toolbar-right">
				{validationResult && !isFinalized && (
					<div className="boq-validation-badge-wrapper" ref={badgeRef}>
						<button
							type="button"
							className={`boq-validation-badge ${validationResult.isSchemaCompliant ? "boq-validation-badge--valid" : "boq-validation-badge--invalid"}`}
							onClick={() => setPanelOpen((v) => !v)}
							title={t("boq.validate.badge-click-hint")}
						>
							<span className="boq-validation-badge__icon">
								{validationResult.isSchemaCompliant ? "✓" : "⚠"}
							</span>
							<span className="boq-validation-badge__label">
								{t(validationResult.isSchemaCompliant ? "boq.validate.panel-title-valid" : "boq.validate.panel-title-invalid")}
							</span>
						</button>
						{panelOpen && (
							<BoQValidationPanel
								result={validationResult}
								onClose={() => setPanelOpen(false)}
								onElementClick={(id) => { onValidationElementClick?.(id); setPanelOpen(false); }}
							/>
						)}
					</div>
				)}
				{isFinalized ? (
					<>
						<span className="badge bg-success me-2">{t("boq.toolbar.finalized")}</span>
						{!hasInvoice && (
							<ButtonAtom
								className="btn btn-outline-warning btn-sm"
								onClick={onReopenBoQ ?? noop}
								title={t("boq.toolbar.reopen-info")}
							>
								{t("boq.toolbar.reopen")}
							</ButtonAtom>
						)}
					</>
				) : (
					<>
						{isSaving ? (
							<span className="boq-toolbar-autosave-indicator me-2">
								<Spinner animation="border" size="sm" className="me-1" />
								{t("boq.toolbar.auto-saving")}
							</span>
						) : (
							boqStatus === BOQ_STATUS.DRAFT && (
								<span className="badge bg-secondary me-2">{t("boq.toolbar.draft")}</span>
							)
						)}
						<ButtonAtom
							className="btn btn-outline-secondary btn-sm me-2"
							onClick={onDiscard ?? noop}
							disabled={!hasUnsavedChanges || isSaving}
						>
							{t("boq.toolbar.discard")}
						</ButtonAtom>
						<ButtonAtom
							className="btn btn-outline-primary btn-sm me-2"
							onClick={onSaveDraft ?? noop}
							disabled={!hasUnsavedChanges || isSaving || hasOzErrors}
							title={hasOzErrors ? t("boq.save-blocked-oz-errors") : t("boq.toolbar.save-draft-info")}
						>
							{isSaving ? (
								<Spinner animation="border" size="sm" className="me-1" />
							) : (
								<FontAwesomeIcon icon={faSave} className="me-1" />
							)}
							{t("boq.toolbar.save-draft")}
						</ButtonAtom>
						<ButtonAtom
							className="btn btn-primary btn-sm"
							onClick={onValidateAndFinalize ?? noop}
							disabled={isSaving || isVFValidating || hasOzErrors}
							title={hasOzErrors ? t("boq.save-blocked-oz-errors") : t("boq.validate.and-finalize-info")}
						>
							{(isSaving || isVFValidating) ? (
								<Spinner animation="border" size="sm" className="me-1" />
							) : (
								<FontAwesomeIcon icon={faSave} className="me-1" />
							)}
							{t("boq.validate.and-finalize")}
						</ButtonAtom>
					</>
				)}
				<OverlayTrigger placement="top" overlay={<Tooltip id="boq-toolbar-tour">{t("boq.toolbar.tour-hint")}</Tooltip>}>
					<span className="d-inline-block ms-2">
						<ButtonAtom
							className="boq-toolbar-icon-btn"
							data-tour="boq-tour-trigger"
							onClick={onStartTour ?? noop}
						>
							<FontAwesomeIcon icon={faCircleQuestion} />
						</ButtonAtom>
					</span>
				</OverlayTrigger>
			</div>
		</div>
	);
};

export default BoQToolbar;
